import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CategoryWithParts, ICategorySelectedInMultipleTables, IPart } from "../../../../models/Part";
import { PartCategoryConflictMap } from "../../../../models/PartCategorySubPart";
import PriceFormat from "../../../../utils/priceFormat";
import { formatMinutesIntoHours } from "../../../../utils/formatMinutesIntoHours";
import { ButtonInput } from "../../../shared/Inputs/ButtonInput";
import { CreateJobRequest, JobType } from "../../../../models/Job";
import { ILead } from "../../../../models/Lead";
import { IVehicle } from "../../../../models/Vehicle";
import useDefaultCRUDHandlers from "../../../../hooks/useDefaultCRUDHandlers";
import useJobMutations from "../../../../data/useJobMutations";
import usePartMutations from "../../../../data/usePartMutations";
import { DateTime } from "luxon";
import useRsAutomotive from "../../../../data/useRsAutomotive";
import useRsCoreAccount from "../../../../data/useRsCoreAccount";
import useSettingSet from "../../../../data/useSettingSet";
import Modal from "../../../../layouts/Modal";
import VehicleImagesCard from "../../../vehicles/VehicleImagesCard";


const SummaryCard = ({ title, time, price, total }: { 
  title: string, 
  time?: number | null, 
  price: number,
  total?: number | null
}) => {
  const { t } = useTranslation("leads");

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full">
      <h1 className="text-lg font-semibold">{title}</h1>
      <div className="flex justify-between">
        {typeof time === "number" && (
          <div>
            <p className="text-gray-600 text-sm">{t("Est. Time")}</p>
            <p>{formatMinutesIntoHours(time)}</p>
          </div>
        )}
        {typeof total === "number" && (
          <div>
            <p className="text-gray-600 text-sm">{t("Total")}</p>
            <p>{total}</p>
          </div>
        )}
        <div>
          <p className="text-gray-600 text-sm">{t("Price")}</p>
          <p>{PriceFormat(price)}</p>
        </div>
      </div>
    </div>
  );
};

interface SelectedCategoriesProps {
  selectedCategories: CategoryWithParts[] | undefined;
  setSelectedCategories: any;
  setSelectedByTableId: any;
  lead: ILead | null;
  vehicle: IVehicle | null;
  /** Global (cross-table) main/sub-part clashes, keyed by category id. */
  categoryConflicts?: PartCategoryConflictMap;
  /**
   * Categories ticked in more than one table - a category listed by two assemblies appears in
   * both of their tables. Each selection creates its own part, so this warns rather than
   * blocks: ticking one twice is how the operator asks for two of it.
   */
  categoriesSelectedInMultipleTables?: ICategorySelectedInMultipleTables[];
  /**
   * Lead whose photos "See Images" opens. Separate from `lead` because the vehicle page
   * passes `lead` as null (it drives Create vs Update Job) but still has the car's photos.
   */
  imagesLead?: ILead | null;
}


export default function SelectedCategories({ selectedCategories, lead, vehicle, setSelectedCategories, setSelectedByTableId, categoryConflicts, categoriesSelectedInMultipleTables, imagesLead }: SelectedCategoriesProps) {
  const [imagesOpen, setImagesOpen] = useState(false);
  const { t } = useTranslation("leads");
  const { create } = useJobMutations();
  const { createParts } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Job"));
  const { saveHandlers: partsSaveHandlers } = useDefaultCRUDHandlers(t("Parts"));
  const { isSet: hasRsKey } = useSettingSet("Rs:ApiKey");
  const { order } = useRsAutomotive();
  const { rsCoreAccount } = useRsCoreAccount();
  const [totalTime, setTotalTime] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [bodyTime, setBodyTime] = useState<number>(0);
  const [mechTime, setMechTime] = useState<number>(0);
  const [bodyPrice, setBodyPrice] = useState<number>(0);
  const [mechPrice, setMechPrice] = useState<number>(0);
  
  const [rsTotal, setRsTotal] = useState<number>(0);
  const [rsPrice, setRsPrice] = useState<number>(0);


  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return;
    }

    const asNumberOrZero = (value: number | null | undefined) => {
      if (typeof value !== "number" || Number.isNaN(value)) return 0;
      return value;
    };

    const getCategoryTime = (cat: CategoryWithParts) => {
      return asNumberOrZero(cat.category.whTime) + asNumberOrZero(cat.category.disTime);
    };

    // Calculate total time and price
    setTotalTime(selectedCategories.reduce((sum, cat) => sum + getCategoryTime(cat), 0));
    setTotalPrice(selectedCategories.reduce((sum, cat) => sum + asNumberOrZero(cat.price), 0));

    // Calculate body work time and price
    setBodyTime(selectedCategories.reduce((sum, cat) => sum + (!cat.isMech ? getCategoryTime(cat) : 0), 0));
    setBodyPrice(selectedCategories.reduce((sum, cat) => sum + (!cat.isMech ? asNumberOrZero(cat.price) : 0), 0));

    // Calculate mechanical work time and price
    setMechTime(selectedCategories.reduce((sum, cat) => sum + (cat.isMech ? getCategoryTime(cat) : 0), 0));
    setMechPrice(selectedCategories.reduce((sum, cat) => sum + (cat.isMech ? asNumberOrZero(cat.price) : 0), 0));

    setRsTotal(selectedCategories.filter(cat => cat.isCore).length);
    setRsPrice(selectedCategories.reduce((sum, cat) => sum + (cat.isCore ? asNumberOrZero(cat.category.rsPrice) : 0), 0));

  }, [selectedCategories]);

  // A ref rather than isLoading: a fast double click lands before the re-render that disables
  // the buttons, and each extra click places another RS order.
  const submitting = useRef(false);
  const isSubmitting = order.isLoading || create.isLoading || createParts.isLoading;

  const handleCreateJob = async () => {

    if (!selectedCategories || selectedCategories.length === 0 || submitting.current) return;
    submitting.current = true;

    let rsRes = await handleRsOrder();
    if (rsRes != null && rsRes.rs_status != "success") {
      saveHandlers.onError();
      submitting.current = false;
      return;
    }

    const newJob: CreateJobRequest = {
      type: JobType.Generic, //All jobs are generic now
      title: `${vehicle?.combinedMake} ${vehicle?.modelRangeDescription} - ${vehicle?.vrm}`,
      description: selectedCategories.map(cat => cat.category.name).join(", "),
      duration: totalTime,
      estimatedPrice: totalPrice, //If everything sells at the price entered
      estimatedMargin: null, //I don't think this is useful anymore as we don't have it for new parts
      leadId: lead?.id ?? null,
      vehicleId: vehicle?.id,
      jobPartCategories: selectedCategories.map(cat => ({
        partCategory: cat.category,
        averagePrice: cat.category.averagePrice,
        price: (cat.isCore ? cat.category.rsPrice : cat.price) ?? 0,
        lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
        note: cat.note,
        coreOrderNumber: cat.isCore ? rsRes.rs_data.rs_sales_order_number : null,
        accountId: cat.isCore ? (rsCoreAccount.data?.data?.id ?? null) : (cat.accountId ?? null),
        physicalRouting: cat.physicalRouting ?? null
      })),
    }

    // console.log("new Job", newJob)

    create.mutate(newJob, {
      ...saveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      },
      onSettled: () => {
        submitting.current = false;
      }
    });
  }

  const handleRsOrder = async () => {
    if (!selectedCategories || selectedCategories.length === 0) return null;

    var rsCategories = selectedCategories.filter(sc => sc.isCore);
    if (!rsCategories || rsCategories.length === 0) return null;

    // A failed request (the API returns 502 when RS errors or times out) is handled by the
    // callers like an RS-side rejection.
    try {
      return await order.mutateAsync({
        rs_ktype:  rsCategories[0].category.rsData.rs_ktype,
        rs_regno:  rsCategories[0].category.rsData.rs_regno,
        rs_search: rsCategories[0].category.rsData.rs_search,
        rs_vendor: rsCategories[0].category.rsData.rs_vendor,
        rs_products: Object.fromEntries(
            rsCategories.map((rc) => {
              const categoryName = (rc.category.rsName ?? rc.category.name)?.trim();
              const rsItem = rc.category.rsItem;

              // skip invalid rows
              if (!categoryName || !rsItem) return null;

              return [categoryName, { rs_item: rsItem }] as const;
            }).filter(Boolean) as Array<readonly [string, { rs_item: string }]>
          ),
      });
    } catch {
      return { rs_status: "error" };
    }
  }

  const handleCreateParts = async (isDamaged: boolean) => {
    if (!selectedCategories || selectedCategories.length === 0 || submitting.current) return;
    submitting.current = true;

    let rsRes = await handleRsOrder();
    if (rsRes != null && rsRes.rs_status != "success") {
      saveHandlers.onError();
      submitting.current = false;
      return;
    }

    const newParts = selectedCategories.map(cat => ({
      categoryId: cat.category.id,
      price: (cat.isCore ? cat.category.rsPrice : cat.price) ?? 0,
      averagePrice: cat.category.averagePrice,
      lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
      note: cat.note,
      coreOrderNumber: cat.isCore ? rsRes.rs_data.rs_sales_order_number : null,
      accountId: cat.isCore ? (rsCoreAccount.data?.data?.id ?? null) : (cat.accountId ?? null),
      physicalRouting: cat.physicalRouting ?? null,
      isDamaged
    }));

    const body = {
      vehicleId: vehicle?.id,
      leadId: lead?.id,
      parts: newParts
    }

    // console.log("new parts", newParts);

    createParts.mutate(body, {
      ...partsSaveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      },
      onSettled: () => {
        submitting.current = false;
      }
    });
  }

  const conflictCount = categoryConflicts?.size ?? 0;

  /**
   * Names of the "main" categories involved in a clash - the useful half of the message,
   * since resolving a conflict means deciding between the main and its sub-parts.
   */
  const conflictMainNames = useMemo(() => {
    if (!categoryConflicts || !selectedCategories) return [] as string[];

    return selectedCategories
      .filter((cat) => categoryConflicts.get(cat.category.id)?.role === "main")
      .map((cat) => cat.category.name);
  }, [categoryConflicts, selectedCategories]);

  const duplicateSelections = categoriesSelectedInMultipleTables ?? [];
  const duplicateList = duplicateSelections
    .map((dup) => `${dup.categoryName} (${dup.tableNames.join(", ")})`)
    .join("; ");

  if (!selectedCategories || selectedCategories.length === 0) {
    return null;
  };

  return (
    <>
      <div className="flex flex-col sticky pb-2 top-14 z-30 bg-gray-100">

        <div className="flex items-center gap-x-8 mt-4">

          <div className="bg-white shadow rounded-lg p-4 w-full">

            <div className="flex items-center justify-between gap-x-4">
              <h1 className="text-lg font-semibold">{t("Categories Selected")}</h1>
              {imagesLead?.id && (
                <ButtonInput label={"See Images"} onClick={() => setImagesOpen(true)} classes={undefined} size="sm" />
              )}
            </div>
        
            <p className="text-2xl pt-3 flex items-center gap-x-4">{selectedCategories.length}
              {selectedCategories.some(cat => !cat.price) && (
                <span className="text-gray-600 text-sm"> {t("- {{count}} categories have no price", { count: selectedCategories.filter(cat => !cat.price).length })}</span>
              )}
              {conflictCount > 0 && (
                <span className="text-red-700 text-sm font-semibold">
                  {conflictCount === 1
                    ? t("- {{count}} conflicting category selected", { count: conflictCount })
                    : t("- {{count}} conflicting categories selected", { count: conflictCount })}
                </span>
              )}
            </p>
            {conflictCount > 0 && (
              <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-800">
                {conflictMainNames.length === 1
                  ? t("{{names}} is selected together with parts contained within it. Highlighted in red below - you can still save.", { names: conflictMainNames.join(", ") })
                  : t("{{names}} are selected together with parts contained within them. Highlighted in red below - you can still save.", { names: conflictMainNames.join(", ") })}
              </p>
            )}
            {duplicateSelections.length > 0 && (
              <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
                {duplicateSelections.length === 1
                  ? t("This category is selected in more than one assembly, so a separate part will be created for each selection: {{list}}. Untick one if you only want a single part.", { list: duplicateList })
                  : t("These categories are selected in more than one assembly, so a separate part will be created for each selection: {{list}}. Untick one if you only want a single part.", { list: duplicateList })}
              </p>
            )}
          </div>

          <SummaryCard title={t("Total")} time={totalTime} price={totalPrice} />
          <SummaryCard title={t("Body Work")} time={bodyTime} price={bodyPrice} />
          <SummaryCard title={t("Mechanical Work")} time={mechTime} price={mechPrice} />
          {hasRsKey && (
            <SummaryCard title="RS Automotive" total={rsTotal} price={rsPrice} />
          )}
        </div>

      </div>
      <div className="flex justify-between">
        <div className="flex gap-x-6">
          <ButtonInput label={lead?.jobs && lead.jobs.length > 0 ? t("Update Job") : t("Create Job")} onClick={() => handleCreateJob()} classes={undefined} isLoading={isSubmitting} />
          <ButtonInput label={t("Make Available")} onClick={() => handleCreateParts(false)} classes={undefined} isLoading={isSubmitting} />
        </div>

        <div>
          <ButtonInput label={t("Mark as Damaged")} onClick={() => handleCreateParts(true)} classes={"!bg-red-500 hover:!bg-red-600"} isLoading={isSubmitting} />
        </div>
      </div>

      {imagesLead?.id && (
        <Modal open={imagesOpen} width={"max-w-6xl"} setOpen={setImagesOpen}>
          <VehicleImagesCard lead={imagesLead} inModal />
        </Modal>
      )}
    </>
  )
}